/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faCheckCircle, faGraduationCap, faBriefcase, faUpload } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import aksharLogo from '../assets/FINAL LOGO AK.png';
import ResumeUpload from '../components/ResumeUpload';
import '../styles/JobSeekerSetupChoice.css';

const RoleSetupChoice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userRole, setUserRole] = useState('jobSeeker');

  useEffect(() => {
    // Get user role from localStorage or user context
    const role = user?.role || localStorage.getItem('role') || 'jobSeeker';
    setUserRole(role.toLowerCase());
    
    // Redirect recruiters directly to registration form (they don't get a choice)
    if (role.toLowerCase() === 'recruiter') {
      navigate('/recruiter-registration');
      return;
    }
    
    // Redirect university users directly to registration form (they don't get a choice)
    if (role.toLowerCase() === 'university') {
      navigate('/university-registration');
      return;
    }
    
    // Redirect employer users directly to registration form (they don't get a choice)
    if (role.toLowerCase() === 'employer') {
      navigate('/employer-registration');
      return;
    }
    
    // Redirect NGO users directly to registration form (they don't get a choice)
    if (role.toLowerCase() === 'ngo') {
      navigate('/ngo-registration');
      return;
    }
    
    // Redirect Association users directly to registration form (they don't get a choice)
    if (role.toLowerCase() === 'association') {
      navigate('/association-registration');
      return;
    }
  }, [user, navigate]);

  // Role-specific configuration
  // NOTE: This page is ONLY for Job Seekers and Interns
  // Recruiters are redirected directly to their registration form
  const roleConfig = {
    jobseeker: {
      formRoute: '/jobseeker-registration',
      dashboardRoute: '/jobseeker-dashboard',
      title: 'Job Seeker',
      icon: faBriefcase,
      description: 'AI-powered resume parsing to automatically populate your profile'
    },
    intern: {
      formRoute: '/intern-registration',
      dashboardRoute: '/intern-dashboard',
      title: 'Intern',
      icon: faGraduationCap,
      description: 'AI-powered resume parsing to automatically populate your intern profile'
    }
  };

  const config = roleConfig[userRole] || roleConfig.jobseeker;
  const [showResumeUpload, setShowResumeUpload] = useState(false);

  const handleFillForm = () => {
    // Navigate to role-specific registration form
    navigate(config.formRoute);
  };

  const handleResumeUploaded = async (resumeData, aiRecommendations) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const populateUrl = userRole === 'intern'
        ? buildApiUrl('/api/intern/populate-from-resume')
        : buildApiUrl('/api/jobseeker/populate-from-resume');
      const populateResponse = await axios.post(populateUrl, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (populateResponse.data?.success) {
        setShowResumeUpload(false);
        alert('Resume uploaded and profile populated successfully! Taking you to your dashboard...');
        window.location.href = config.dashboardRoute;
      } else {
        throw new Error(populateResponse.data?.error || 'Failed to populate profile');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      setShowResumeUpload(false);
      alert(`Resume uploaded successfully, but profile population failed:\n\n${errorMessage}\n\nYou can manually edit your profile from the dashboard.`);
      window.location.href = config.dashboardRoute;
    }
  };

  return (
    <div className="setup-choice-container">
      <div className="setup-choice-content">
        <div className="setup-choice-header">
          <img src={aksharLogo} alt="AksharJobs Logo" className="header-logo" />
          <h1>Welcome to AksharJobs!</h1>
          <p>Welcome, {user?.firstName || 'there'}! Let's set up your {config.title.toLowerCase()} profile. Choose how you'd like to proceed:</p>
        </div>

        <div className="setup-options">
          <div className="setup-option-card" onClick={handleFillForm}>
            <div className="option-icon">
              <FontAwesomeIcon icon={faFileAlt} />
            </div>
            <h3>Create Profile</h3>
            <p>Manually create your profile by filling in all your details</p>
            <div className="option-features">
              <div className="feature-item">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Complete control over all fields</span>
              </div>
              <div className="feature-item">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Step-by-step guidance</span>
              </div>
              <div className="feature-item">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Save and resume anytime</span>
              </div>
            </div>
            <button className="option-button">Create Profile</button>
          </div>

          <div className="setup-option-card" onClick={() => setShowResumeUpload(true)}>
            <div className="option-icon upload-icon">
              <FontAwesomeIcon icon={faUpload} />
            </div>
            <h3>Upload Resume</h3>
            <p>{config.description}</p>
            <div className="option-features">
              <div className="feature-item">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>AI extracts your info automatically</span>
              </div>
              <div className="feature-item">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Profile pre-filled in seconds</span>
              </div>
              <div className="feature-item">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>PDF, DOCX, or image supported</span>
              </div>
            </div>
            <button className="option-button">Upload Resume</button>
          </div>
        </div>

        <div className="setup-choice-footer">
          <p>You can always update your profile later from your dashboard</p>
        </div>
      </div>

      {showResumeUpload && (
        <ResumeUpload
          onResumeUploaded={handleResumeUploaded}
          onClose={() => setShowResumeUpload(false)}
        />
      )}
    </div>
  );
};

export default RoleSetupChoice;

